"use client";
import React, { useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { motion } from "framer-motion";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  browserSessionPersistence,
  browserLocalPersistence,
  UserCredential,
  sendPasswordResetEmail,
} from "firebase/auth";

import Logo from "@/app/_media/Logo.svg";
import NextImage from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import {
  useMantineTheme,
  Button,
  Checkbox,
  Text,
  Flex,
  Group,
  PasswordInput,
  Paper,
  Stack,
  Image,
  TextInput,
  Title,
  Box,
  Anchor,
  Portal,
  Modal,
} from "@mantine/core";

import ThemeToggle from "@/app/_components/ThemeToggle/ThemeToggle";

import PageShell from "@/app/_components/PageShell/PageShell";
import AnimatedLoader from "@/app/_components/AnimatedLoader/AnimatedLoader";
import { createUser, getUser } from "@/app/_firebase/user";
import {
  blockedSignupEmailMessage,
  isBlockedSignupEmail,
  parseSignupIntent,
} from "@/app/_constants/signup";
import { markNewSignupHandoff, consumeNewSignupHandoff } from "@/app/_utils/signupHandoff";
import { trackAccountCreated } from "@/app/_utils/marketingConversions";

const Login = () => {
  const auth = getAuth();
  const theme = useMantineTheme();
  const searchParams = useSearchParams();
  const createAccountParam = searchParams.get("createaccount");
  const selection = parseSignupIntent(searchParams);

  const [isCreateAccount, setIsCreateAccount] = useState(
    createAccountParam === "true"
  );
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const [showResetModal, setShowResetModal] = useState(false);
  useEffect(() => {
    setIsCreateAccount(createAccountParam === "true");
  }, [createAccountParam]);
  // Extract user and client parameters
  const form = useForm({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
      keepLoggedIn: true,
      acceptTerms: false,
    },
    validate: {
      email: (val) =>
        !/^\S+@\S+$/.test(val)
          ? "Invalid email"
          : isCreateAccount && isBlockedSignupEmail(val)
            ? blockedSignupEmailMessage
            : null,
      password: (val) =>
        val.length <= 6
          ? "Password should include at least 6 characters"
          : null,
      confirmPassword: (val, values) =>
        isCreateAccount
          ? val === values.password
            ? null
            : "Passwords do not match"
          : null,
      acceptTerms: (val) =>
        isCreateAccount && !val
          ? "You must accept the privacy policy and EULA to create an account"
          : null,
    },
  });

  const handleEmailLogin = async (isCreateAccount: boolean) => {
    try {
      await auth.setPersistence(
        form.values.keepLoggedIn
          ? browserLocalPersistence
          : browserSessionPersistence
      );

      if (isCreateAccount) {
        // Also checked here so a scripted submit can't skip form validation.
        if (isBlockedSignupEmail(form.values.email)) {
          setIsLoading(false);
          form.setFieldError("email", blockedSignupEmailMessage);
          return;
        }
        const createdUserCredentials = await createUserWithEmailAndPassword(
          auth,
          form.values.email,
          form.values.password
        );

        // Mark before writing the profile: the layout can observe that write
        // and mount /signup before createUser finishes setting auth claims.
        markNewSignupHandoff(createdUserCredentials.user.uid);
        await createUser(
          createdUserCredentials.user.uid,
          createdUserCredentials.user.email!,
          selection,
          () => trackAccountCreated(createdUserCredentials.user.uid)
        );
        // The layout observes the new pending profile and sends it to checkout.
        // A second redirect here could re-trigger checkout after that handoff.
      } else {
        const userCredential: UserCredential = await signInWithEmailAndPassword(
          auth,
          form.values.email,
          form.values.password
        );
        // Recover if authentication succeeded on a previous attempt but the
        // initial profile write was interrupted. Existing shops are untouched.
        const profile = await getUser(userCredential.user.uid);
        if (!profile) {
          await createUser(
            userCredential.user.uid,
            userCredential.user.email!,
            selection,
            () => trackAccountCreated(userCredential.user.uid)
          );
        }
      }
    } catch (error: any) {
      if (isCreateAccount && auth.currentUser) {
        consumeNewSignupHandoff(auth.currentUser.uid);
      }
      if (error.code === "auth/invalid-credential") {
        setIsLoading(false);
        form.setErrors({
          email: "Email/Password Incorrect",
          password: "Email/Password Incorrect",
        });
      } else if (error.code === "auth/email-already-in-use") {
        setIsLoading(false);
        form.setFieldError("email", "An account already exists with this email. Choose ‘Already have an account?’ to sign in and continue.");
      } else {
        setIsLoading(false);
        form.setErrors({
          email: " ",
          password:
            "Oops, something went wrong! Please try again or contact support.",
          confirmPassword: " ",
        });
      }
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await auth.setPersistence(
        form.values.keepLoggedIn
          ? browserLocalPersistence
          : browserSessionPersistence
      );

      // TODO: handle user can create an account but if they haven't paid they should be redirected too?
      const userCredential = await signInWithPopup(auth, provider);
    } catch (error) {
      // TOOD: handle error
      console.error("Error logging in with Google:", error);
      setIsLoading(false);
    }
  };

  return (
    <PageShell>
      <Box h="100%" w="100%" pos="relative">
        {isLoading && (
          <Portal>
            <AnimatedLoader bkgOpacity={1} />
          </Portal>
        )}
        <Box pos="absolute" right={0} top={0}>
          <ThemeToggle />
        </Box>
        <Flex justify="center" align="center" h="100%" w="100%">
          <Stack align="center" gap="xl">
            <Stack align="center" gap="xs">
              <motion.div
                initial={{
                  opacity: 0,
                  scale: 0.5,
                }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1 }}
              >
                <Image
                  component={NextImage}
                  src={Logo}
                  alt="MountMonitor"
                  w="auto"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.75 }}
              >
                <Title
                  style={{ textAlign: "center" }}
                  order={1}
                  c={theme.colors.primary[5]}
                >
                  MountMonitor
                </Title>
              </motion.div>
            </Stack>
            {/* <Typewriter text="Welcome back" speed={1} delay={0.75} /> */}
            <motion.div
              initial={{ y: "50px", opacity: 0 }}
              animate={{ y: "0px", opacity: 1 }}
              transition={{ duration: 1, delay: 0.75 }}
            >
              <Paper radius="sm" w={{ base: "350px", lg: "500px" }}>
                {/* <Group grow mb="md" mt="md">
                  <GoogleButton
                    onClick={() => {
                      setIsLoading(true);
                      handleGoogleLogin();
                    }}
                  >
                    Login with Google
                  </GoogleButton>
                </Group>

                <Divider
                  label="Or login with email"
                  labelPosition="center"
                  my="lg"
                  color="dimmed"
                /> */}

                <form
                  onSubmit={form.onSubmit((vals) => {
                    setIsLoading(true);
                    handleEmailLogin(isCreateAccount);
                  })}
                >
                  <Stack gap="md">
                    <TextInput
                      required
                      size="lg"
                      type="email"
                      name="email"
                      label="Email"
                      autoComplete="email"
                      placeholder="e.g admin@highcountrytaxidermy.com"
                      value={form.values.email}
                      onChange={(event) =>
                        form.setFieldValue("email", event.currentTarget.value)
                      }
                      error={form.errors.email}
                    />

                    {!isPasswordReset && (
                      <PasswordInput
                        required
                        size="lg"
                        type="password"
                        name="password"
                        autoComplete="password"
                        label="Password"
                        placeholder="Your password"
                        value={form.values.password}
                        onChange={(event) =>
                          form.setFieldValue(
                            "password",
                            event.currentTarget.value
                          )
                        }
                        error={form.errors.password}
                      />
                    )}
                    {isCreateAccount && !isPasswordReset && (
                      <PasswordInput
                        required
                        size="lg"
                        type="password"
                        name="confirmPassword"
                        label="Confirm Password"
                        placeholder="Confirm password"
                        value={form.values.confirmPassword}
                        onChange={(event) =>
                          form.setFieldValue(
                            "confirmPassword",
                            event.currentTarget.value
                          )
                        }
                        error={form.errors.confirmPassword}
                      />
                    )}
                    <Stack align="start">
                      {isCreateAccount && (
                        <Checkbox
                          size="sm"
                          label={
                            <Text size="xs">
                              I accept the{" "}
                              <Anchor
                                href="https://www.mountmonitor.com/privacy"
                                target="_blank"
                                component="a"
                              >
                                privacy policy
                              </Anchor>{" "}
                              and{" "}
                              <Anchor
                                href="https://www.mountmonitor.com/eula"
                                target="_blank"
                                component="a"
                              >
                                end user license agreement
                              </Anchor>
                            </Text>
                          }
                          {...form.getInputProps("acceptTerms", {
                            type: "checkbox",
                          })}
                          error={form.errors.acceptTerms}
                        />
                      )}
                      <Group
                        justify="space-between"
                        align="center"
                        my="sm"
                        w="100%"
                      >
                        {!isPasswordReset && (
                          <Checkbox
                            label="Keep me logged in"
                            size="md"
                            {...form.getInputProps("keepLoggedIn", {
                              type: "checkbox",
                            })}
                          />
                        )}
                        {isCreateAccount ? (
                          <Anchor
                            component="button"
                            type="button"
                            onClick={() => {
                              const params = new URLSearchParams(searchParams.toString());
                              params.delete("createaccount");
                              router.push(`/login${params.size ? `?${params}` : ""}`);
                              setIsCreateAccount(false);
                              setIsPasswordReset(false);
                              form.reset();
                            }}
                            mt="4"
                            size="xs"
                          >
                            Already have an account?
                          </Anchor>
                        ) : isPasswordReset ? (
                          <Anchor
                            ml="auto"
                            component="button"
                            type="button"
                            onClick={() => {
                              setIsPasswordReset(false);
                            }}
                            size="xs"
                          >
                            Back to Login
                          </Anchor>
                        ) : (
                          <Anchor
                            component="button"
                            type="button"
                            onClick={() => {
                              setIsPasswordReset(true);
                            }}
                            size="xs"
                          >
                            Forgot Password?
                          </Anchor>
                          // <Anchor
                          //   component="button"
                          //   type="button"
                          //   onClick={() => {
                          //     router.push("/login?createaccount=true");
                          //     setIsCreateAccount(true);
                          //     form.reset();
                          //   }}
                          //   size="xs"
                          // >
                          //   Don&apos;t have an account?
                          // </Anchor>
                        )}
                      </Group>{" "}
                    </Stack>
                    {isPasswordReset ? (
                      <Button
                        type="button"
                        size="lg"
                        onClick={async () => {
                          try {
                            await sendPasswordResetEmail(
                              auth,
                              form.values.email
                            );
                            setShowResetModal(true);
                          } catch (error) {
                            // Handle error if needed
                            console.error("Error sending reset email:", error);
                          }
                        }}
                      >
                        Reset Password
                      </Button>
                    ) : (
                      <Button type="submit" size="lg">
                        {isCreateAccount ? "Create Account" : "Login"}
                      </Button>
                    )}
                  </Stack>
                </form>
              </Paper>
            </motion.div>
          </Stack>
        </Flex>
      </Box>
      <Modal
        opened={showResetModal}
        onClose={() => {
          setShowResetModal(false);
          setIsPasswordReset(false);
        }}
        title="Check Your Email"
        centered
      >
        <Stack>
          <Text>
            If an account exists with this email address, you will receive a
            password reset link shortly.
          </Text>
          <Button
            onClick={() => {
              setShowResetModal(false);
              setIsPasswordReset(false);
            }}
          >
            Got it
          </Button>
        </Stack>
      </Modal>
    </PageShell>
  );
};

export default Login;
